/**
 * The one engagement naming rule, for both halves of the app (CommonJS so the
 * API can require() it and the React build can import it):
 *
 *   "Entity Name - UEN - FYE31-12-2025 - External Audit"
 *   "Entity Name - UEN - FYE31-12-2025 - Agreed Upon Procedures #2"
 *
 * Segments after the FYE: the Service Provided, then " #n" only for services
 * flagged "several engagements per company and FYE" in Dropdown Lists
 * (lookup_options.allow_multiple_per_fye) — the first engagement of the
 * company, FYE and service carries no number, the second is #2, the third #3
 * (services/engagementSlot.resolve). Services without the flag never show one.
 *
 * Used when an engagement is created (registration approval, Management
 * create, EAR add-company), when the live name of an existing engagement is
 * derived for display (server/utils/clientNow.js) and when a name is taken
 * apart for display (components/workflow/CoverageDisplay.js). The stored
 * engagement_name is a snapshot taken at creation; a client rename never
 * rewrites it, a Service Provided rename does (it is the firm's own list).
 *
 * parseEngagementName also reads the older two-segment form
 * ("Entity - UEN - FYE…", no service) so snapshots written before the service
 * tail existed still split correctly.
 */

const FYE_SEG_RE = /^FYE(\d{2}-\d{2}-\d{4})$/;
const SEQ_TAIL_RE = /^(.*) #(\d+)$/;

// 'YYYY-MM-DD' → 'DD-MM-YYYY'; anything else passes through untouched.
const fyeDisplay = (yearEnd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yearEnd || '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : yearEnd;
};

/**
 * @param {string} entityName
 * @param {string} uen
 * @param {string} yearEnd   'YYYY-MM-DD'
 * @param {string|null} service   Service Provided (omitted → no service tail)
 * @param {number|null} seq       the engagement's number among same-service
 *                                engagements of that company and FYE; null
 *                                for services that allow only one
 */
const composeEngagementName = (entityName, uen, yearEnd, service = null, seq = null) => {
  let name = `${String(entityName).trim()} - ${String(uen).trim()} - FYE${fyeDisplay(yearEnd)}`;
  const svc = service === null || service === undefined ? '' : String(service).trim();
  if (svc) {
    name += ` - ${svc}`;
    if (seq !== null && seq !== undefined && seq !== '') name += ` #${seq}`;
  }
  return name;
};

/**
 * "Entity - UEN - FYE31-12-2025 - Service #n" →
 *   { entity, uen, fye: '31-12-2025', service, seq, composed: true }
 * The FYE segment is found from the tail (last segment matching FYE…), so an
 * entity name containing " - " still splits correctly. Anything that does not
 * follow the rule comes back as { entity: <whole string>, composed: false }.
 */
const parseEngagementName = (name) => {
  const s = String(name || '');
  const parts = s.split(' - ');
  let fyeIdx = -1;
  for (let i = parts.length - 1; i >= 2; i -= 1) {
    if (FYE_SEG_RE.test(parts[i].trim())) { fyeIdx = i; break; }
  }
  if (fyeIdx < 0) return { entity: s, uen: '', fye: '', service: '', seq: null, composed: false };
  const fye = FYE_SEG_RE.exec(parts[fyeIdx].trim())[1];
  let service = parts.slice(fyeIdx + 1).join(' - ').trim();
  let seq = null;
  const m = SEQ_TAIL_RE.exec(service);
  if (m) { service = m[1].trim(); seq = parseInt(m[2], 10); }
  return {
    entity: parts.slice(0, fyeIdx - 1).join(' - ').trim(),
    uen: parts[fyeIdx - 1].trim(),
    fye,
    service,
    seq,
    composed: true,
  };
};

/** The service tail as people see it: "Agreed Upon Procedures #2" / "External Audit" / ''. */
const serviceLabel = (service, seq) => {
  const svc = service === null || service === undefined ? '' : String(service).trim();
  if (!svc) return '';
  return seq !== null && seq !== undefined && seq !== '' ? `${svc} #${seq}` : svc;
};

/** The first line of a Description of Service (the form asks for a short subject first). */
const descriptionHeadline = (text) => String(text || '').split(/\r?\n/)[0].trim();

/**
 * What tells an engagement apart from the company's other engagements of the
 * same FYE: "Agreed Upon Procedures #2 · Grant claim audit".
 *
 * `serviceDescription` is the API field of that name — the Description of
 * Service only for services flagged allow_multiple_per_fye, null for every
 * other service (server/utils/serviceDescription.js). Every surface that
 * names an engagement prints this tag (or engagementDisplayName) instead of
 * assembling service and description itself.
 */
const engagementTag = (service, seq, serviceDescription) =>
  [serviceLabel(service, seq), descriptionHeadline(serviceDescription)].filter(Boolean).join(' · ');

/** The composed name plus the description headline, for plain text (mails, PDFs, messages). */
const engagementDisplayName = (engagementName, serviceDescription) => {
  const name = String(engagementName || '');
  const head = descriptionHeadline(serviceDescription);
  return head ? `${name} · ${head}` : name;
};

module.exports = {
  composeEngagementName, parseEngagementName, serviceLabel, fyeDisplay,
  descriptionHeadline, engagementTag, engagementDisplayName,
};
